import { formatNumber } from '@angular/common'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { Store, select } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { Map } from 'immutable'
import { Subscription } from 'rxjs'
import {
  FetchBusinessGeneralRequest,
  FetchPropertyGeneralRequest,
} from './dashboard.actions'
import {
  getBusinessGeneralState,
  getPropertyGeneralState,
} from './dashboard.selectors'
import { NgxPermissionsService } from 'ngx-permissions'

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  public tooltipsFormatter = {
    tooltip: {
      callbacks: {
        // title: () => null,
        label: function (tooltipItem: any, data: any) {
          return (
            ' ' +
            formatNumber(tooltipItem.dataset.data[tooltipItem.dataIndex], 'en')
          )
        },
      },
    },
  }
  public scalesFormatter = {
    scales: {
      y: {
        ticks: {
          callback: function (value: number) {
            return formatNumber(value, 'en')
          },
        },
      },
    },
  }
  public activeTab: string = 'properties'
  private propertyGeneralState$: Subscription
  private businessGeneralState$: Subscription
  private propertyGeneral: Map<string, any>
  private businessGeneral: Map<string, any>

  public propertyCountByPropertyTypeChart: any
  public propertyExpectedVsActualRevenueChart: any
  public propertyExpectedTotalRevenueChart: any
  public propertyExpectedAverageRevenueChart: any
  public propertyTotal: any = {
    totalItems: 0,
    totalExpectedRevenue: 0,
    totalCollectedRevenue: 0,
    percentage: 0,
  }
  public businessCountByPropertyTypeChart: any
  public businessExpectedVsActualRevenueChart: any
  public businessExpectedTotalRevenueChart: any
  public businessExpectedAverageRevenueChart: any
  public businessTotal: any = {
    totalItems: 0,
    totalExpectedRevenue: 0,
    totalCollectedRevenue: 0,
    percentage: 0,
  }

  constructor(
    private store: Store<any>,
    private translateService: TranslateService,
    public permissionsService: NgxPermissionsService,
  ) {}

  ngOnInit() {
    this.activeTab = this.permissionsService.getPermissions()[
      'DASHBOARD_PROPERTY_TAB'
    ]
      ? 'properties'
      : 'licensing'
    this.propertyGeneralState$ = this.store
      .pipe(select(getPropertyGeneralState))
      .subscribe((state) => {
        this.propertyGeneral = state.get('data')

        if (this.propertyGeneral.size) {
          this.propertyCountByPropertyTypeChart = this.countByPropertyType(
            this.propertyGeneral,
          )
          this.propertyExpectedVsActualRevenueChart =
            this.expectedVsActualRevenue(this.propertyGeneral)
          this.propertyExpectedTotalRevenueChart = this.expectedTotalRevenue(
            this.propertyGeneral,
          )
          this.propertyExpectedAverageRevenueChart =
            this.expectedAverageRevenue(this.propertyGeneral)
          this.propertyTotal = this.total(this.propertyGeneral)
        }
      })

    this.businessGeneralState$ = this.store
      .pipe(select(getBusinessGeneralState))
      .subscribe((state) => {
        this.businessGeneral = state.get('data')

        if (this.businessGeneral.size) {
          this.businessCountByPropertyTypeChart = this.countByPropertyType(
            this.businessGeneral,
            `${this.translateService.instant('INDUSTRY')}`,
            `${this.translateService.instant('COUNT_BY_INDUSTRY')}`,
          )
          this.businessExpectedVsActualRevenueChart =
            this.expectedVsActualRevenue(this.businessGeneral)
          this.businessExpectedTotalRevenueChart = this.expectedTotalRevenue(
            this.businessGeneral,
          )
          this.businessExpectedAverageRevenueChart =
            this.expectedAverageRevenue(this.businessGeneral)
          this.businessTotal = this.total(this.businessGeneral)
        }
      })

    this.store.dispatch<any>(new FetchPropertyGeneralRequest())
    this.store.dispatch<any>(new FetchBusinessGeneralRequest())
  }

  public setTab(index: string) {
    this.activeTab = index
  }

  ngOnDestroy() {
    this.propertyGeneralState$.unsubscribe()
    this.businessGeneralState$.unsubscribe()
  }

  private countByPropertyType(
    source: Map<string, any>,
    key: string = 'type',
    title: string = `${this.translateService.instant(
      'COUNT_BY_PROPERTY_TYPE',
    )}`,
  ) {
    const data = source.getIn(['count', 'list']) as any
    const options = {
      plugins: {
        title: {
          display: true,
          text: title,
        },
        ...this.tooltipsFormatter,
      },
      aspectRatio: 1.5,
    }

    return {
      values: {
        labels: data.map((item: Map<string, any>) => item.get(key)).toJS(),
        datasets: [
          {
            data: data
              .map((item: Map<string, any>) => item.get('count'))
              .toJS(),
          },
        ],
      },
      options,
    }
  }

  private expectedVsActualRevenue(source: Map<string, any>) {
    const data = source.getIn(['financial', 'list']) as any
    const options = {
      ...this.scalesFormatter,
      plugins: {
        title: {
          display: true,
          text: `${this.translateService.instant(
            'EXPECTED_VS_ACTUAL_REVENUE',
          )}`,
        },
        ...this.tooltipsFormatter,
      },
      aspectRatio: 1.5,
    }

    return {
      labels: data.map((item: Map<string, any>) => item.get('name')).toJS(),
      options,
      datasets: [
        {
          label: 'Expected',
          borderWidth: 1,
          data: data
            .map((item: Map<string, any>) => item.get('expected'))
            .toJS(),
        },
        {
          label: 'Actual',
          borderWidth: 1,
          data: data.map((item: Map<string, any>) => item.get('actual')).toJS(),
        },
      ],
    }
  }

  private expectedTotalRevenue(source: Map<string, any>) {
    const data = source.getIn(['financial', 'list']) as any
    const options = {
      plugins: {
        title: {
          display: true,
          text: `${this.translateService.instant('EXPECTED_TOTAL_REVENUE')}`,
        },
        ...this.tooltipsFormatter,
      },
      aspectRatio: 1.5,
    }

    return {
      values: {
        labels: data.map((item: Map<string, any>) => item.get('name')).toJS(),
        datasets: [
          {
            data: data
              .map((item: Map<string, any>) => item.get('expected'))
              .toJS(),
          },
        ],
      },
      options,
    }
  }

  private expectedAverageRevenue(source: Map<string, any>) {
    const data = source.getIn(['financial', 'list']) as any
    const options = {
      plugins: {
        title: {
          display: true,
          text: `${this.translateService.instant('EXPECTED_AVERAGE_REVENUE')}`,
        },
        ...this.tooltipsFormatter,
      },
      aspectRatio: 1.5,
    }

    return {
      values: {
        labels: data.map((item: Map<string, any>) => item.get('name')).toJS(),
        datasets: [
          {
            data: data
              .map((item: Map<string, any>) => item.get('avgExpected'))
              .toJS(),
          },
        ],
      },
      options,
    }
  }

  private total(source: Map<string, any>) {
    const data = source.getIn(['financial', 'aggregates']) as any

    return {
      totalItems: source.getIn(['count', 'total']),
      totalExpectedRevenue: data.get('totalExpected'),
      totalCollectedRevenue: data.get('totalActual'),
      percentage: data.get('percentage'),
    }
  }
}
